import React from 'react';
import PropTypes, {
  bool,
  func,
  shape,
  string,
} from 'prop-types';
import { Button, Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexShopSmallIntroBanner.scss';

const AmexShopSmallIntroBanner = ((
  {
    eventTiming,
    introConfig,
    isAuthenticated,
    handleTagging,
    login,
    scrollTo,
    showSignupModal,
    tagConfig,
    toggleTerms,
    baseRequestUrl,
  }
) => {
  const {
    left_box: leftBox,
    right_box: rightBox,
  } = introConfig;

  const {
    intro_banner: {
      email_opt_in: {
        event_name: emailOptInEventName,
        utm: emailOptInUtm,
      },
      create_account: {
        event_name: createAccountEventName,
      },
      login: {
        event_name: loginEventName,
        category: loginCategory,
        label: loginLabel,
      },
      enroll: {
        event_name: enrollEventName,
      },
      editorials: {
        event_name: finRestaurantsEventName,
      },
      learn_more: {
        event_name: learnMoreEventName,
        utm: learMoreUtm,
      },
    },
  } = tagConfig;
  const leftBoxData = isAuthenticated ? leftBox[eventTiming]?.logged_in : leftBox[eventTiming]?.logged_out;
  const rightBoxData = rightBox[eventTiming];
  const {
    email_opt_in: optInLink,
  } = leftBoxData;

  const handleScroll = (url) => {
    const currentHash = url.replace('#', '');
    toggleTerms(currentHash);
    setTimeout(() => {
      scrollTo(url);
    }, 100);
  };

  const displayCTA = (cta) => {
    const { label, url } = cta;
    const query = {
      ...emailOptInUtm,
    };

    switch (cta.name) {
      case 'terms':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--text-link terms-link"
            key={label}
            href={url}
            variant="no-style"
            onClick={() => { handleScroll(url); }}
          >
            {label}
          </Link>
        );
      case 'enroll':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--link enroll-link"
            href={url}
            target="_blank"
            key={label}
            onClick={() => { handleTagging(enrollEventName); }}
          >
            {label}
          </Link>
        );
      case 'learnMore':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--link learnmore-link"
            href={`${url}${learMoreUtm}`}
            target="_blank"
            key={label}
            onClick={() => { handleTagging(learnMoreEventName); }}
          >
            {label}
          </Link>
        );
      case 'editorials':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--link editorials-link"
            variant="no-style"
            href={url}
            key={label}
            onClick={() => {
              window.location.hash = url.replace('#', '');
              scrollTo(url);
              handleTagging(finRestaurantsEventName);
            }}
          >
            {label}
          </Link>
        );
      case 'optIn':
        return (
          <Link
            className={`AmexShopSmallIntroBanner__content--${eventTiming === 'during' ? 'text-link' : 'primary'} optin-link`}
            href={{ pathname: url, query }}
            target="_blank"
            key={label}
            onClick={() => { handleTagging(emailOptInEventName); }}
          >
            {label}
          </Link>
        );
      case 'explore':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--link explore-link"
            href={url}
            key={label}
            onClick={() => {
              window.location.hash = url.replace('#', '');
              scrollTo(url);
            }}
          >
            {label}
          </Link>
        );
      case 'whyMatter':
        return (
          <Link
            className="AmexShopSmallIntroBanner__content--link whymatter-link"
            href={url}
            key={label}
            onClick={() => {
              window.location.hash = url.replace('#', '');
              scrollTo(url);
            }}
          >
            {label}
          </Link>
        );
      case 'login':
        return (
          <Button
            variant="no-style"
            className="AmexShopSmallIntroBanner__content--outline login-link"
            key={label}
            onClick={() => { login(loginEventName, loginCategory, loginLabel); }}
          >
            {label}
          </Button>
        );
      case 'account':
        return (
          <Button
            variant="primary"
            type="button"
            className="AmexShopSmallIntroBanner__content--primary signup-link"
            key={label}
            onClick={() => { showSignupModal(); handleTagging(createAccountEventName); }}
            data-link="create-account"
          >
            {label}
          </Button>
        );
      default:
        return null;
    }
  };

  const displayRightBox = (data = {}) => {
    const { title, subtitle, text } = data;
    const sanitizedTitle = sanitizeString(title);
    const sanitizedSubtitle = sanitizeString(subtitle);
    const sanitizedText = sanitizeString(text);

    return (
      <li className="AmexShopSmallIntroBanner__boxes--item">
        <div className="AmexShopSmallIntroBanner__content">
          <h2
            className="AmexShopSmallIntroBanner__content--title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedTitle,
            }}
          />
          <h3
            className="AmexShopSmallIntroBanner__content--subtitle"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSubtitle,
            }}
          />
          {(eventTiming === 'after') ? (
            <div className="AmexShopSmallIntroBanner__content--withimage">
              <img
                src={`${baseRequestUrl}ShopSmall/editorialData/hitlist/${data.image}`}
                alt=""
              />
              <div className="AmexShopSmallIntroBanner__content--withimage-body">
                <p className="AmexShopSmallIntroBanner__content--text AmexShopSmallIntroBanner__content--withimage-text">{data.text}</p>
                <p className="AmexShopSmallIntroBanner__content--withlink">
                  {(data.ctas.length > 0) && data.ctas.map((cta) => displayCTA(cta))}
                </p>
              </div>
            </div>
          ) : (
            <>
              <p>
                <span
                  className="AmexShopSmallIntroBanner__content--text"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedText,
                  }}
                />
                {data.terms_apply && displayCTA(data.terms_apply)}
              </p>
              {(data.ctas && data.ctas.length > 0) && (
                <div className="AmexShopSmallIntroBanner__content__button-container">
                  {data.ctas.map((cta) => displayCTA(cta))}
                </div>
              )}
            </>

          )}

        </div>
      </li>
    );
  };

  const displayLeftBox = (data = {}) => {
    const {
      title, subtitle, text, text_1: text1, text_2: text2,
    } = data;
    const sanitizedTitle = sanitizeString(title);
    const sanitizedSubtitle = sanitizeString(subtitle);
    const sanitizedText = sanitizeString(text);
    const sanitizedText1 = sanitizeString(text1);
    const sanitizedText2 = sanitizeString(text2);

    return (
      <li className="AmexShopSmallIntroBanner__boxes--item">
        <div className="AmexShopSmallIntroBanner__content AmexShopSmallIntroBanner__vertical-space">
          <h2
            className="AmexShopSmallIntroBanner__content--title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedTitle,
            }}
          />
          <h3
            className="AmexShopSmallIntroBanner__content--subtitle"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSubtitle,
            }}
          />
          {data.text ? (
            <p
              className="AmexShopSmallIntroBanner__content--text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedText,
              }}
            />
          ) : (
            <p className="AmexShopSmallIntroBanner__content--text">
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedText1,
                }}
              />
              {displayCTA(optInLink)}
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedText2,
                }}
              />
            </p>
          )}
          <div className="AmexShopSmallIntroBanner__content__button-container">
            {(data.ctas && data.ctas.length > 0) && data.ctas.map((cta) => displayCTA(cta))}
          </div>
        </div>
      </li>
    );
  };

  return (
    <section className="AmexShopSmallIntroBanner">
      <div className="container">
        <ul className="AmexShopSmallIntroBanner__boxes">
          {(eventTiming === 'after') ? (
            <>
              {displayLeftBox(leftBoxData)}
              {displayRightBox(rightBoxData)}
            </>
          ) : (
            <>
              {displayRightBox(rightBoxData)}
              {displayLeftBox(leftBoxData)}
            </>
          )}
        </ul>
      </div>
    </section>
  );
});

AmexShopSmallIntroBanner.propTypes = {
  eventTiming: PropTypes.string.isRequired,
  isAuthenticated: bool.isRequired,
  introConfig: shape({
    left_box: shape({}),
    right_box: shape({}),
  }).isRequired,
  handleTagging: func.isRequired,
  login: func.isRequired,
  scrollTo: func.isRequired,
  showSignupModal: func.isRequired,
  tagConfig: shape({
    intro_banner: shape({
      email_opt_in: shape({
        event_name: string,
        utm: shape({}),
      }),
      create_account: shape({
        event_name: string,
      }),
      enroll: shape({
        event_name: string,
      }),
      editorials: shape({
        event_name: string,
      }),
      login: shape({
        event_name: string,
      }),
      learn_more: shape({
        event_name: string,
        utm: string,
      }),
    }),
  }).isRequired,
  toggleTerms: func.isRequired,
  baseRequestUrl: string.isRequired,
};

export default AmexShopSmallIntroBanner;
